import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Edit,
  Trash2,
  Calendar,
  Stethoscope,
  AlertTriangle,
  FileText,
  History
} from 'lucide-react';
import { MedicalRecordWithRelations } from '../../types/medicalRecord';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthHeaders } from '../../utils/authUtils';

interface MedicalRecordDetailProps {
  petId?: string;
  recordId?: string;
}

const MedicalRecordDetail: React.FC<MedicalRecordDetailProps> = ({ petId: propPetId, recordId: propRecordId }) => {
  const params = useParams<{ petId: string; recordId: string }>();
  const petId = propPetId || params.petId;
  const recordId = propRecordId || params.recordId;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [record, setRecord] = useState<MedicalRecordWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (recordId) {
      fetchRecord();
    }
  }, [recordId]);

  const fetchRecord = async () => {
    if (!recordId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_ENDPOINTS.MEDICAL_RECORDS.GET(recordId), {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied to this medical record');
        }
        if (response.status === 404) {
          throw new Error('Medical record not found');
        }
        throw new Error('Failed to fetch medical record');
      }

      const recordData = await response.json();
      setRecord(recordData);
    } catch (err) {
      console.error('Error fetching medical record:', err);
      setError(err instanceof Error ? err.message : 'Failed to load medical record');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!recordId || isDeleting) return;

    try {
      setIsDeleting(true);

      const response = await fetch(API_ENDPOINTS.MEDICAL_RECORDS.DELETE(recordId), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete medical record');
      }

      // Navigate back to pet detail page
      navigate(`/dashboard/pets/${petId}`);
    } catch (err) {
      console.error('Error deleting medical record:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete medical record');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const canEdit = () => {
    if (!record || !user) return false;
    return user.role === 'ADMIN' ||
      (user.role === 'VET_STAFF' && record.created_by_user_id === user.id);
  };

  const canDelete = () => {
    return user?.role === 'ADMIN';
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading medical record...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/dashboard/pets/${petId}`)}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Pet
          </button>
        </div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Record Not Found</h2>
          <p className="text-gray-600 mb-4">The requested medical record could not be found.</p>
          <button
            onClick={() => navigate(`/dashboard/pets/${petId}`)}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Pet
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{record.pet.name}'s Medical Summary</h1>

              <div className="flex items-center text-gray-600 space-x-6">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>{formatDateTime(record.visit_date)}</span>
                </div>
                <div className="flex items-center">
                  <Stethoscope className="w-4 h-4 mr-2" />
                  <Link
                    to={`/dashboard/pets/${record.pet_id}`}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {record.pet.name} ({record.pet.species})
                  </Link>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3 ml-6">
              <Link
                to={`/dashboard/pets/${petId}/medical-records/history`}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors duration-200"
              >
                <History className="w-4 h-4 mr-2" />
                View History
              </Link>
              {canEdit() && record.is_current && (
                <Link
                  to={`/dashboard/pets/${petId}/medical-records/create?copy_from_id=${record.id}`}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Record
                </Link>
              )}
              {canDelete() && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors duration-200"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Description - Medical Summary */}
        <div className="max-w-4xl">
          {record.description ? (
            <div className="bg-white shadow-sm rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{record.description}</p>
            </div>
          ) : (
            <div className="bg-white shadow-sm rounded-lg p-6">
              <p className="text-gray-500 italic">No medical summary available for this visit.</p>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center mb-4">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                </div>

                <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                  Delete Medical Record
                </h3>

                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to delete this medical record? This action cannot be undone.
                </p>

                <div className="flex justify-center space-x-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isDeleting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                  >
                    {isDeleting ? 'Deleting...' : 'Delete Record'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicalRecordDetail;